import { AvSubsystemAvServiceType } from "@minervaproject/avsubsystem-core";
import clsx from "clsx";
import { FC, useCallback } from "react";

import { useAppSelector } from "../../store/index";
import {
  selectActiveClassSession,
  selectIsClassSessionVirtual,
  selectIsPresentationActive,
} from "../../store/selectors/class-session";
import { selectCurrentSectionTitle } from "../../store/selectors/section";

import { TD } from "./Table";

import { serverStartRecordingSession, serverStopRecordingSession } from "api/api-server-actions.ts";
import {
  selectActiveRecordingSessionForCurrentSection,
  selectCurrentSectionId,
  selectOrgNameBySectionId,
  selectVirtualClassAvService,
  selectVirtualClassLicodeServerUrl,
  userSelectors,
} from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { buildDatadogFacetedUrl } from "utils/monitoring";
import { sectionHomeUrl } from "utils/urls";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

const SectionInfoHeader: FC = () => {
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  const isPresenting = useAppSelector(selectIsPresentationActive);
  const avService = useAppSelector(selectVirtualClassAvService);
  return (
    <thead>
      <tr>
        <th className="!pl-0">Section ID</th>
        <th>Title</th>
        <th>Org</th>
        <th>Status</th>
        {isPresenting && <th>Presenting Page ID</th>}
        {isPresenting && <th>Presenting User</th>}
        {isVirtualClass && (
          <>
            <th>A/V Service</th>
            {avService === AvSubsystemAvServiceType.Licode && <th>Licode Server</th>}
          </>
        )}
        <th>Links</th>
        {isVirtualClass && <th>Recording Controls</th>}
      </tr>
    </thead>
  );
};

const SectionInfoRow: FC<{ sectionId: string }> = ({ sectionId }) => {
  const avService = useAppSelector(selectVirtualClassAvService) as AvSubsystemAvServiceType;
  const licodeServer = useAppSelector(selectVirtualClassLicodeServerUrl);
  const orgName = useAppSelector((state) => selectOrgNameBySectionId(state, sectionId));
  const sectionTitle = useAppSelector(selectCurrentSectionTitle);
  const classSession = useObjectSelector(selectActiveClassSession, [
    "category",
    "presented_page_id",
    "presenting_user_id",
  ]);
  const isVirtualClass = useAppSelector(selectIsClassSessionVirtual);
  const isPresenting = useAppSelector(selectIsPresentationActive);
  const status = isPresenting ? "Presenting" : "Not Presenting";
  const presentingPageId = classSession?.presented_page_id;
  const presentingUser = useObjectSelector(
    (state) => userSelectors.selectById(state, classSession?.presenting_user_id),
    PROFILE_PROPERTIES,
  );
  const activeRecordingSession = useObjectSelector(selectActiveRecordingSessionForCurrentSection, [
    "id",
  ]);

  const onClickStartRecording = useCallback(async () => {
    if (window.confirm("Start recording?")) {
      await serverStartRecordingSession(sectionId);
    }
  }, [sectionId]);

  const onClickStopRecording = useCallback(async () => {
    if (!activeRecordingSession?.id) return;
    if (window.confirm("Stop recording?")) {
      await serverStopRecordingSession(activeRecordingSession?.id);
    }
  }, [activeRecordingSession?.id]);

  const onClickRestartRecording = useCallback(async () => {
    if (!activeRecordingSession?.id) return;
    if (window.confirm("Restart recording?")) {
      await serverStopRecordingSession(activeRecordingSession?.id);
      await serverStartRecordingSession(sectionId);
    }
  }, [activeRecordingSession?.id, sectionId]);

  return (
    <tr>
      <TD dataTestId="section-id">{sectionId}</TD>
      <TD dataTestId="section-title">{sectionTitle}</TD>
      <TD dataTestId="org-name">{orgName}</TD>
      <TD className={clsx({ "text-green-shade-20": isPresenting })} dataTestId="status">
        {status}
      </TD>
      {isPresenting && <TD dataTestId="presenting-page-id">{presentingPageId}</TD>}
      {isPresenting && <TD dataTestId="presenting-user">{getUserFullName(presentingUser)}</TD>}
      {isVirtualClass && (
        <>
          <TD>{avService}</TD>
          {avService === AvSubsystemAvServiceType.Licode && <TD>{licodeServer}</TD>}
        </>
      )}
      <TD className="flex gap-2 text-lg" noWrap>
        <a href={sectionHomeUrl(sectionId)} rel="noreferrer" target="_blank">
          👀
        </a>
        <a href={buildDatadogFacetedUrl({ sectionId })} rel="noreferrer" target="_blank">
          🐕
        </a>
      </TD>
      {isVirtualClass && (
        <TD dataTestId="recording-controls">
          {activeRecordingSession ? (
            <>
              <span className="mr-2 cursor-pointer" onClick={onClickStopRecording}>
                ⏹️ Stop
              </span>
              <span className="cursor-pointer" onClick={onClickRestartRecording}>
                🔁 Restart
              </span>
            </>
          ) : (
            <span className="cursor-pointer" onClick={onClickStartRecording}>
              ⏺️ Start
            </span>
          )}
        </TD>
      )}
    </tr>
  );
};

export const SectionInfo: FC = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  if (!sectionId) return null;
  return (
    <table className="mds-table table-hover" data-testid="section-stats-table">
      <SectionInfoHeader />
      <tbody>
        <SectionInfoRow sectionId={sectionId} />
      </tbody>
    </table>
  );
};
